/**
 * TransactionStats - Real-time Statistics Display
 * 
 * Shows aggregated stats for filtered transactions
 * OPTIMIZED with React.memo
 */

import { useMemo, memo, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { TrendingUp, TrendingDown, ArrowLeftRight, Activity, Calendar } from 'lucide-react';
import { formatCurrency, formatCurrencyCompact, sumWithConversion } from '../../../lib/utils/format';
import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency';
import { useCompanyContext } from '../../../contexts/CompanyContext';
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { cn } from '../../../components/ui/utils';
import type { CashFlowEntry } from '../../../types/database';
import { motion } from 'motion/react';
import { useResponsive } from '../../../hooks/useResponsive';

interface TransactionStatsProps {
  transactions: CashFlowEntry[];
}

function TransactionStatsComponent({ transactions }: TransactionStatsProps) {
  const companyCurrency = useCompanyCurrency();
  const { selectedCompanies } = useCompanyContext();
  const baseCurrency = selectedCompanies[0]?.default_currency || 'BRL';
  const { rates: ratesArray, isLoading: ratesLoading } = useExchangeRates();
  const { isMobile } = useResponsive();
  
  // Converter array de rates para objeto Record<string, number>
  const rates = useMemo(() => {
    if (!ratesArray || ratesArray.length === 0) return null;
    
    // Criar um mapa de taxas de câmbio
    // Formato: { 'USD': 1.1, 'BRL': 6.1, etc }
    // Assumindo que baseCurrency -> targetCurrency
    const ratesMap: Record<string, number> = {};
    
    // Adicionar taxa 1:1 para a moeda base
    ratesMap[baseCurrency] = 1;
    
    // Adicionar todas as taxas que têm baseCurrency como origem
    ratesArray
      .filter(r => r.baseCurrency === baseCurrency)
      .forEach(r => {
        ratesMap[r.targetCurrency] = r.rate;
      });
    
    // Adicionar taxas inversas (se targetCurrency = baseCurrency, inverter a taxa)
    ratesArray
      .filter(r => r.targetCurrency === baseCurrency && r.rate !== 0)
      .forEach(r => {
        if (!ratesMap[r.baseCurrency]) {
          ratesMap[r.baseCurrency] = 1 / r.rate;
        }
      });
    
    return ratesMap;
  }, [ratesArray, baseCurrency]);
  
  // DEBUG: Log para verificar se a conversão está sendo aplicada
  console.log('[TransactionStats] 💰 Mostrando estatísticas das transações filtradas:', {
    baseCurrency,
    companyCurrency,
    hasRates: !!rates && Object.keys(rates).length > 0,
    ratesCount: rates ? Object.keys(rates).length : 0,
    ratesArray: ratesArray?.length || 0,
    ratesLoading,
    ratesMap: rates,
    selectedCompaniesCount: selectedCompanies.length,
    transactionsCount: transactions.length,
  });
  
  // Calculate stats - FIXED: Now uses transactions directly (already filtered from parent)
  const stats = useMemo(() => {
    // Usar sumWithConversion para converter todas as moedas para a moeda base
    const income = sumWithConversion(
      transactions.filter(t => t.entry_type === 'income'),
      baseCurrency,
      rates
    );

    const expense = sumWithConversion(
      transactions.filter(t => t.entry_type === 'expense'),
      baseCurrency,
      rates
    );

    const transfer = sumWithConversion(
      transactions.filter(t => t.entry_type === 'transfer'),
      baseCurrency,
      rates
    );

    const balance = income - expense;
    
    // Filtrar apenas despesas para comparação com Cenários
    const expenseTransactions = transactions.filter(t => t.entry_type === 'expense');
    
    // DEBUG: Log para verificar os valores calculados
    console.log('[TransactionStats] 📊 Calculated stats:', {
      income,
      expense,
      transfer,
      balance,
      count: transactions.length,
      hasRates: !!rates,
      ratesCount: rates ? Object.keys(rates).length : 0,
      sampleTransactions: transactions.slice(0, 3).map(t => ({
        amount: t.amount,
        currency: t.currency,
        type: t.entry_type,
      })),
    });
    
    console.log('[TransactionStats] 💰 DESPESAS PARA COMPARAÇÃO COM CENÁRIOS:', {
      totalExpense: expense.toFixed(2),
      expenseCount: expenseTransactions.length,
      expenseIds: expenseTransactions.map(t => t.id),
      top5Expenses: expenseTransactions
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5)
        .map(t => ({
          id: t.id,
          description: t.description,
          amount: t.amount.toFixed(2),
          currency: t.currency,
          status: t.payment_status,
          effectiveDate: t.payment_date || t.due_date || t.entry_date,
      })),
    });

    return {
      income,
      expense,
      transfer,
      balance,
      count: transactions.length,
    };
  }, [transactions, baseCurrency, rates]);

  const statCards = [
    {
      label: 'Receitas',
      value: stats.income,
      icon: TrendingUp,
      color: 'text-success-600',
      bgColor: 'bg-success-50',
      borderColor: 'border-success-200',
    },
    {
      label: 'Despesas',
      value: stats.expense,
      icon: TrendingDown,
      color: 'text-danger-600',
      bgColor: 'bg-danger-50',
      borderColor: 'border-danger-200',
    },
    {
      label: 'Saldo',
      value: stats.balance,
      icon: Activity,
      color: stats.balance >= 0 ? 'text-success-600' : 'text-danger-600',
      bgColor: stats.balance >= 0 ? 'bg-success-50' : 'bg-danger-50',
      borderColor: stats.balance >= 0 ? 'border-success-200' : 'border-danger-200',
    },
    {
      label: 'Transferências',
      value: stats.transfer,
      icon: ArrowLeftRight,
      color: 'text-info-600',
      bgColor: 'bg-info-50',
      borderColor: 'border-info-200',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Cards de Estatísticas - Now respects parent filters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card 
                variant="interactive"
                className={cn(
                  'p-4 border-2',
                  stat.borderColor
                )}
                role="region"
                aria-label={`Estatística de ${stat.label}: ${formatCurrency(stat.value, companyCurrency)}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground font-medium">
                      {stat.label}
                    </p>
                    <p className={cn(
                      'font-bold tabular-nums truncate',
                      isMobile ? 'text-lg' : 'text-2xl',
                      stat.color
                    )}>
                      {isMobile ? formatCurrencyCompact(stat.value) : formatCurrency(stat.value, companyCurrency)}
                    </p>
                  </div>
                  <div className={cn('p-2 rounded-lg flex-shrink-0', stat.bgColor)}>
                    <Icon className={cn('h-5 w-5', stat.color)} />
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
// Using default shallow comparison - will re-render when transactions array reference changes
// This ensures stats are always accurate when filters change
export const TransactionStats = memo(TransactionStatsComponent);
